import {ReactElement, useEffect, useMemo} from 'react';
import {useQuery as useBackendQuery} from 'react-query';
import {Image as ImageType} from '@shopify/hydrogen/storefront-api-types';

import {useModalToggle, useShopifyProductMetafields} from '../hooks';
import ProductPreview from './ProductPreview.client';
import ProductUserReviewBlock from './ProductUserReviewBlock.client';
import ProductInfo from './ProductInfo.client';

import {getMyProduct} from '../backendApi';
import {FlattenedProduct} from '../utils/graphql';
import {useAuthService} from '../contexts/AuthServiceProvider.client';
import {MemberProductProvider} from '../contexts/MemberProductProvider.client';
import {ClientAnalytics} from '@shopify/hydrogen';
import {TwlAnalyticsEvent, VARIANT_DEFAULT_TITLE} from '../constants';
import {
  getCheapestAvailableVariant,
  getIsVariantAvailableForSale,
  getShopifyProductVariantMetafields,
  getViableTwlShopVariant,
} from '../utils/variants';
import ProductDetailProductReviewModal from './modals/ProductDetailProductReviewModal.client';
import ProductMarketplacePricingModal from './modals/ProductMarketplacePricingModal.client';

interface ProductDetailContainerProps {
  product: FlattenedProduct;
  sampleProduct?: FlattenedProduct;
}

export default function ProductDetailContainer({
  product,
  sampleProduct,
}: ProductDetailContainerProps): ReactElement {
  const {
    openModal: openEditReviewModal,
    closeModal: closeEditReviewModal,
    isOpen: isEditReviewModalOpen,
  } = useModalToggle('editReview');

  const {
    openModal: openMarketplacePricingModal,
    closeModal: closeMarketplacePricingModal,
    isOpen: isMarketplacePricingModal,
  } = useModalToggle('productMarketplacePricing');

  const {metafieldsKeyValue} = useShopifyProductMetafields(product.metafields);
  const {authUser} = useAuthService();
  const isLoginRequired = useMemo(() => !authUser, [authUser]);

  const {data, refetch} = useBackendQuery(
    ['myMemberProduct', metafieldsKeyValue.uuid],
    () => getMyProduct(metafieldsKeyValue.uuid),
    {retry: 1, enabled: !isLoginRequired && Boolean(metafieldsKeyValue.uuid)},
  );

  useEffect(() => {
    ClientAnalytics.publish(TwlAnalyticsEvent.VIEWED_PRODUCT, true, {
      ...ClientAnalytics.getPageAnalyticsData(),
      productId: product.id,
    });
  }, [product.id]);

  const twlShopVariant = useMemo(
    () => getViableTwlShopVariant(product.variants),
    [product.variants],
  );

  const nonTwlPartnerVariants = useMemo(() => {
    return product.variants
      .filter(({id, title, metafields}) => {
        const isTwlShopVariant = !!twlShopVariant && id === twlShopVariant.id;
        return (
          !isTwlShopVariant &&
          title !== VARIANT_DEFAULT_TITLE &&
          getShopifyProductVariantMetafields(metafields).sellerShopIsPartner
        );
      })
      .sort((a, b) => {
        const aIsForSale = getIsVariantAvailableForSale(a);
        const bIsForSale = getIsVariantAvailableForSale(b);
        return aIsForSale === bIsForSale
          ? Number(a.price.amount) - Number(b.price.amount)
          : Number(bIsForSale) - Number(aIsForSale);
      });
  }, [product, twlShopVariant]);

  const minPricedProductVariant = useMemo(() => {
    return twlShopVariant || getCheapestAvailableVariant(nonTwlPartnerVariants);
  }, [twlShopVariant, nonTwlPartnerVariants]);

  return (
    <MemberProductProvider
      key={product.id}
      memberProduct={data}
      refetch={refetch}
    >
      <ProductPreview
        product={product}
        sampleProduct={sampleProduct}
        openEditReviewModal={openEditReviewModal}
        openMarketplacePricingModal={openMarketplacePricingModal}
        twlShopVariant={twlShopVariant}
        minPricedProductVariant={minPricedProductVariant}
        nonTwlPartnerVariants={nonTwlPartnerVariants}
      />
      <div className="separator mt-50 mb-75" />
      {metafieldsKeyValue.uuid && (
        <ProductUserReviewBlock
          openEditReviewModal={openEditReviewModal}
          productUuid={metafieldsKeyValue.uuid}
        />
      )}
      <ProductInfo
        metafields={product.metafields}
        descriptionHtml={product.descriptionHtml}
      />
      <ProductDetailProductReviewModal
        isOpen={isEditReviewModalOpen}
        onRequestClose={closeEditReviewModal}
        shopifyProductId={product.id}
        productTitle={product.title}
        productImage={product.featuredImage as ImageType}
      />
      <ProductMarketplacePricingModal
        isOpen={isMarketplacePricingModal}
        onRequestClose={closeMarketplacePricingModal}
        minPricedProductVariant={minPricedProductVariant}
        partnerVariants={nonTwlPartnerVariants}
      />
    </MemberProductProvider>
  );
}
